import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";

/**
 * Confirmation modal for provider to finalize cancellation of a purchased policy.
 * @param {boolean} open - Whether the modal is visible
 * @param {function} onClose - Called when user closes or cancels
 * @param {function} onConfirm - Called when user confirms finalize cancel
 * @param {string} policyId - Policy/purchase ID (e.g. insurance_policy_slug) for display
 * @param {string} customerName - Customer name for context
 * @param {boolean} isSubmitting - Optional; disables confirm button while request is in progress
 */
const ConfirmCancelModal = ({
  open,
  onClose,
  onConfirm,
  policyId,
  customerName,
  isSubmitting = false,
}) => {
  const displayId =
    policyId && policyId !== "N/A" && policyId.length > 20
      ? `${policyId.substring(0, 20)}...`
      : policyId;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex fixed inset-0 z-50 justify-center items-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 backdrop-blur-sm bg-black/40"
            onClick={isSubmitting ? undefined : onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden relative z-10 w-full max-w-md bg-white rounded-2xl ring-1 ring-gray-100 shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-red-500 to-rose-600" />
            <div className="p-6">
              <div className="flex gap-3 items-start">
                <div className="grid place-items-center flex-shrink-0 w-11 h-11 text-white bg-gradient-to-br from-amber-500 to-rose-600 rounded-xl shadow-lg">
                  <AlertTriangle size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    Finalize cancellation?
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    You are about to finalize the cancellation for policy{" "}
                    <span
                      className="font-mono font-semibold text-gray-900"
                      title={policyId && policyId !== "N/A" ? policyId : undefined}
                    >
                      {displayId || "—"}
                    </span>
                    {customerName && customerName !== "Unknown" ? (
                      <>
                        {" "}
                        (customer: <span className="font-semibold text-gray-900">{customerName}</span>)
                      </>
                    ) : null}
                    . This will mark the purchase as cancelled. This action cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="grid place-items-center flex-shrink-0 w-9 h-9 text-gray-500 rounded-lg hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mt-5 w-full h-px bg-gradient-to-r from-transparent via-gray-100 to-transparent" />

              <div className="flex gap-3 justify-end mt-4">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 rounded-xl ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                >
                  Keep Pending
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-md bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-rose-400/50 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {isSubmitting ? "Finalizing…" : "Yes, finalize cancellation"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmCancelModal;
